import React, { useState, useCallback } from "react";
import Ripple from "@components/atoms/Ripple";
import Loading from "@components/atoms/Loading";
import {
  makeConfig,
  makeRedirectUri,
  sendLog,
  makeAuthorizationEndpoint,
  SupportType,
  ActionType,
  InstagramAuthProvider,
} from "@lib/util/oauth";
import * as Linking from "expo-linking";
import { graphql, fetchQuery } from "react-relay/hooks";
import { OAuthLinkInstagramLoginEnabledQuery } from "@generated/OAuthLinkInstagramLoginEnabledQuery.graphql";
import Colors from "@constants/Colors";
import message from "@hooks/useMessage";
import { clickAction } from "@lib/util/analytics";
import InstagramGuide from "@components/molecules/InstagramGuide";
import { SnsTextGeneration } from "@lib/util/snsTextGeneration";
import RelayEnvironment from "../../../RelayEnvironment";

// 認証方式は管理画面のフラグで切り替える。
const instagramLoginEnabledQuery = graphql`
  query OAuthLinkInstagramLoginEnabledQuery {
    instagramLoginEnabled
  }
`;

async function fetchInstagramAuthProvider(): Promise<InstagramAuthProvider> {
  const result = await fetchQuery<OAuthLinkInstagramLoginEnabledQuery>(
    RelayEnvironment,
    instagramLoginEnabledQuery,
    {},
    { fetchPolicy: "network-only" }
  ).toPromise();
  return result?.instagramLoginEnabled === true
    ? "INSTAGRAM_LOGIN"
    : "FACEBOOK_LOGIN";
}

type Props = {
  type: SupportType;
  action: ActionType;
  callbackUrl?: string;
  children: React.ReactNode;
  useGuide?: boolean;
};

export default function OAuthLink({
  type,
  action,
  callbackUrl,
  children,
  useGuide = false,
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  // nullならガイドを閉じる。
  const [guideProvider, setGuideProvider] =
    useState<InstagramAuthProvider | null>(null);
  const { set: setMessage } = message();

  const openAuthorizationEndpoint = useCallback(
    async (instagramAuthProvider?: InstagramAuthProvider): Promise<void> => {
      try {
        if (loading) {
          return;
        }
        setLoading(true);
        sendLog(type);
        const config = makeConfig(type, { instagramAuthProvider });
        const url = await makeAuthorizationEndpoint({
          ...config,
          platform: type,
          callbackUrl,
          action,
          redirectUri: makeRedirectUri(),
        });
        await Linking.openURL(url);
      } catch (e) {
        setMessage({
          title: `${SnsTextGeneration(type)}連携できませんでした`,
          mode: "toast",
        });
      } finally {
        setLoading(false);
      }
    },
    [type, loading, action, callbackUrl, setMessage]
  );

  const onPress = useCallback(async (): Promise<void> => {
    if (type !== "Instagram") {
      openAuthorizationEndpoint();
      return;
    }

    let provider: InstagramAuthProvider;
    try {
      setLoading(true);
      provider = await fetchInstagramAuthProvider();
    } catch (e) {
      setMessage({
        title: `${SnsTextGeneration(type)}連携できませんでした`,
        mode: "toast",
      });
      return;
    } finally {
      setLoading(false);
    }

    if (useGuide) {
      clickAction("OpenInstagramGuide");
      setGuideProvider(provider);
      return;
    }
    openAuthorizationEndpoint(provider);
  }, [useGuide, type, openAuthorizationEndpoint, setMessage]);

  return (
    <>
      <Ripple disabled={loading} onPress={onPress} rippleColor={Colors.white}>
        {children}
        {loading && <Loading mask />}
      </Ripple>
      {guideProvider !== null && (
        <InstagramGuide
          authProvider={guideProvider}
          onClose={() => setGuideProvider(null)}
          onConfirmed={() => {
            openAuthorizationEndpoint(guideProvider);
            setGuideProvider(null);
          }}
        />
      )}
    </>
  );
}
